import { isoDate } from '@/lib/dates'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useMyRequests, useRejoinMutations } from '../api'
import { LATE_TREATMENT_LABELS } from '../types'
import type { LateTreatment, RequestView } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

function addDays(date: string, days: number): string {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return isoDate(next)
}

/**
 * Confirming that somebody actually came back.
 *
 * <p>The form changes shape with the date, and that is the whole design. Coming back early
 * releases days; coming back late is a decision somebody has to make — an approved
 * extension, unpaid, or absence — and the form refuses to submit until it is made, because
 * leaving those days undecided is precisely how they get paid as though the person were at
 * work.
 */
export function RejoinDialog({
  request,
  onConfirmed,
  onCancel,
}: {
  request: RequestView
  onConfirmed?: () => void
  onCancel?: () => void
}) {
  const planned = addDays(request.toDate, 1)
  const { confirm } = useRejoinMutations()
  const { data: allRequests } = useMyRequests()

  const [actual, setActual] = useState(planned)
  const [lateTreatment, setLateTreatment] = useState<LateTreatment | ''>('')
  const [extensionRequestId, setExtensionRequestId] = useState('')
  const [remarks, setRemarks] = useState('')
  const [error, setError] = useState<string | null>(null)

  const outcome = actual < planned ? 'EARLY' : actual > planned ? 'LATE' : 'ON_TIME'
  const varianceDays = Math.round(
    (new Date(actual).getTime() - new Date(planned).getTime()) / 86_400_000,
  )

  // Only an approved leave for the same person that starts after this one could cover the
  // gap; offering anything else would invite a choice the backend then refuses.
  const candidateExtensions = (allRequests ?? []).filter(
    (candidate) =>
      candidate.id !== request.id &&
      candidate.status === 'APPROVED' &&
      candidate.fromDate > request.toDate,
  )

  /** Kept out of the JSX so the submit handler itself returns void, not a promise. */
  async function send() {
    setError(null)
    try {
      await confirm.mutateAsync({
        id: request.id,
        actualReturnDate: actual,
        lateTreatment: outcome === 'LATE' ? lateTreatment || undefined : undefined,
        extensionRequestId:
          lateTreatment === 'EXTENSION_APPROVED' ? extensionRequestId || undefined : undefined,
        remarks: remarks || undefined,
      })
      onConfirmed?.()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That return could not be confirmed')
    }
  }

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault()
        void send()
      }}
    >
      <div>
        <h3 className="text-base font-semibold text-[var(--color-heading)]">
          Confirm return — {request.leaveTypeName}
        </h3>
        <p className="text-sm text-[var(--color-muted)]">
          {request.fromDate} to {request.toDate}, due back {planned}.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="actual-return" required>
          Actually returned on
        </Label>
        <DatePicker
          id="actual-return"
          required
          value={actual}
          onChange={(event) => setActual(event.target.value)}
          className="w-52"
        />
        <p className="text-sm text-[var(--color-muted)]" role="status">
          {outcome === 'ON_TIME' && 'Back as planned. Nothing changes on the balance.'}
          {outcome === 'EARLY' &&
            `Back ${Math.abs(varianceDays)} day(s) early — the days not taken go back to the balance.`}
          {outcome === 'LATE' &&
            `Back ${varianceDays} day(s) late — say what those days are below.`}
        </p>
      </div>

      {outcome === 'LATE' && (
        <div className="space-y-3 rounded-[var(--radius-control)] border border-[var(--color-border)] p-3">
          <div className="space-y-1.5">
            <Label htmlFor="late-treatment" required>
              The extra days are
            </Label>
            <Select
              id="late-treatment"
              required
              value={lateTreatment}
              onChange={(event) => setLateTreatment(event.target.value as LateTreatment)}
              className="w-full"
            >
              <option value="">Choose…</option>
              {(Object.keys(LATE_TREATMENT_LABELS) as LateTreatment[]).map((key) => (
                <option key={key} value={key}>
                  {LATE_TREATMENT_LABELS[key]}
                </option>
              ))}
            </Select>
          </div>

          {lateTreatment === 'EXTENSION_APPROVED' && (
            <div className="space-y-1.5">
              <Label htmlFor="extension-request" required>
                Which extension
              </Label>
              <Select
                id="extension-request"
                required
                value={extensionRequestId}
                onChange={(event) => setExtensionRequestId(event.target.value)}
                className="w-full"
              >
                <option value="">Choose…</option>
                {candidateExtensions.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.leaveTypeName}: {candidate.fromDate} to {candidate.toDate}
                  </option>
                ))}
              </Select>
              {candidateExtensions.length === 0 && (
                <p className="text-xs text-[var(--color-muted)]">
                  No approved leave follows this one. The extension has to be applied for and
                  approved first — this screen cannot grant leave.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="rejoin-remarks">Notes</Label>
        <Input
          id="rejoin-remarks"
          value={remarks}
          onChange={(event) => setRemarks(event.target.value)}
          placeholder="Optional"
        />
      </div>

      {error && (
        <p role="alert" className="text-sm text-[var(--color-danger)]">
          {error}
        </p>
      )}

      <div className="flex gap-2">
        <Button type="submit" disabled={confirm.isPending}>
          {confirm.isPending ? 'Confirming…' : 'Confirm return'}
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  )
}
